import { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { toast } from "@/src/common/snackbar"
import { ConfirmDialog } from "@/src/common/custom-dialog"
import { DashboardContent } from "@/src/layouts/dashboard"
import { getInternalWorkspaces } from "@/src/services/internal"
import {
    deleteWorkspacePromptOverride,
    fetchPromptPolicies,
    fetchWorkspaceOverrides,
} from "@/src/services/promptPolicies"
import { ModifyPromptPolicyActions } from "@/src/features/internalUsers/components/ModifyPromptPolicyActions"
import { PromptPolicyCompareDialog, type PromptPolicyCompareState } from "@/src/features/internalUsers/components/PromptPolicyCompareDialog"
import { PromptPolicyVersionHistoryDialog } from "@/src/features/internalUsers/components/PromptPolicyVersionHistoryDialog"
import { WorkspacePromptOverridesDialog } from "@/src/features/internalUsers/components/WorkspacePromptOverridesDialog"
import {
    WorkspacePromptPreviewDialog,
    type PromptPreviewState,
} from "@/src/features/internalUsers/components/WorkspacePromptPreviewDialog"
import { PromptPoliciesGuidanceAlert } from "@/src/features/internalUsers/components/PromptPoliciesGuidanceAlert"
import {
    formatPolicyTypeName,
    formatTimestamp,
    getEffectivePrompt,
    getStaleOverrideTooltip,
    isOverrideStale,
    mapInternalWorkspaceOption,
    mergeWorkspaceNameMap,
    resolveWorkspaceDisplayName,
    type WorkspaceOption,
} from "@/src/features/internalUsers/utils/promptPolicyUtils"
import type { InternalWorkspacesResponseData } from "@/src/store/slices/internal.slice"
import type { PromptPolicy, WorkspacePromptPolicyOverride } from "@/src/types/promptPolicy"

import Autocomplete from "@mui/material/Autocomplete"
import Box from "@mui/material/Box"
import Card from "@mui/material/Card"
import Chip from "@mui/material/Chip"
import Stack from "@mui/material/Stack"
import Alert from "@mui/material/Alert"
import Skeleton from "@mui/material/Skeleton"
import Typography from "@mui/material/Typography"
import Button from "@mui/material/Button"
import TextField from "@mui/material/TextField"
import Divider from "@mui/material/Divider"
import Tooltip from "@mui/material/Tooltip"
import { Iconify } from "@/src/common/iconify"
import { LoadingButton } from "@mui/lab"

// ----------------------------------------------------------------------

const POLICY_TYPE_COLOR_MAP: Record<string, "primary" | "secondary" | "success" | "warning" | "info" | "error"> = {
    CRITERIA_GENERATION_PROMPT: "primary",
    INTERVIEW_QA_GENERATION_PROMPT: "secondary",
    SCRIPT_GENERATION_PROMPT: "success",
}

type OverridesLoadState = {
    overrides: WorkspacePromptPolicyOverride[]
    error?: string
    loading?: boolean
}

// ----------------------------------------------------------------------

type PromptPolicyCardProps = {
    policy: PromptPolicy
    globalPolicies: Record<string, PromptPolicy>
    selectedWorkspaceId: string
    selectedWorkspaceName: string
    overridesState: OverridesLoadState
    workspaceNameById: Record<string, string>
    onSaveSuccess: () => void
    onRetryOverrides: () => void
    onOpenVersionHistory: (policy: PromptPolicy) => void
    onOpenWorkspaceOverrides: (policy: PromptPolicy) => void
    onPreviewWorkspaceOverride: (policy: PromptPolicy, override: WorkspacePromptPolicyOverride) => void
    onPreviewGlobalPrompt: (policy: PromptPolicy) => void
    onPreviewEffective: (policy: PromptPolicy) => void
    onCompare: (policy: PromptPolicy) => void
    onResetToGlobal: (policy: PromptPolicy) => void
}

const PromptPolicyCard = ({
    policy,
    globalPolicies,
    selectedWorkspaceId,
    selectedWorkspaceName,
    overridesState,
    workspaceNameById,
    onSaveSuccess,
    onRetryOverrides,
    onOpenVersionHistory,
    onOpenWorkspaceOverrides,
    onPreviewWorkspaceOverride,
    onPreviewGlobalPrompt,
    onPreviewEffective,
    onCompare,
    onResetToGlobal,
}: PromptPolicyCardProps) => {
    const chipColor = POLICY_TYPE_COLOR_MAP[policy.policyType] ?? "info"
    const { overrides, error: overridesError, loading: overridesLoading } = overridesState
    const overrideCount = overrides.length
    const globalVersion = policy.currentGlobalVersionNumber
    const selectedOverride = overrides.find((o) => o.workspaceId === selectedWorkspaceId)
    const hasWorkspaceOverride = !!selectedOverride
    const selectedOverrideStale =
        selectedOverride && isOverrideStale(selectedOverride, globalVersion)

    return (
        <Card sx={{ p: { xs: 2.5, md: 3 } }}>
            <Stack spacing={2}>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    justifyContent="space-between"
                    spacing={1.5}
                >
                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                        <Typography variant="subtitle1">{formatPolicyTypeName(policy.policyType)}</Typography>
                        <Chip label={policy.policyType} color={chipColor} size="small" variant="outlined" />
                        {globalVersion != null && globalVersion > 0 && (
                            <Chip label={`Global v${globalVersion}`} size="small" color="info" variant="soft" />
                        )}
                        {selectedWorkspaceId && hasWorkspaceOverride && (
                            <Chip
                                label={
                                    selectedOverrideStale ? "Custom for this workspace (stale)" : "Custom for this workspace"
                                }
                                color={selectedOverrideStale ? "error" : "warning"}
                                size="small"
                                variant="soft"
                            />
                        )}
                    </Stack>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Iconify icon="mdi:history" />}
                        onClick={() => onOpenVersionHistory(policy)}
                        sx={{ flexShrink: 0 }}
                    >
                        Version history
                    </Button>
                </Stack>

                <Stack spacing={0.5}>
                    <Typography variant="caption" color="text.secondary">
                        {policy.description}
                    </Typography>
                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                        <Typography variant="caption" color="text.secondary">
                            Global last modified: {formatTimestamp(policy.lastModifiedDate)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Global created: {formatTimestamp(policy.createdDate)}
                        </Typography>
                    </Stack>
                </Stack>

                {overridesError && (
                    <Alert
                        severity="warning"
                        action={
                            <Button color="inherit" size="small" onClick={onRetryOverrides}>
                                Retry
                            </Button>
                        }
                    >
                        Could not load workspace overrides: {overridesError}
                    </Alert>
                )}

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Iconify icon="mdi:earth" />}
                        onClick={() => onPreviewGlobalPrompt(policy)}
                    >
                        View global prompt
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Iconify icon="mdi:eye-outline" />}
                        disabled={!selectedWorkspaceId}
                        onClick={() => onPreviewEffective(policy)}
                    >
                        View effective prompt
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Iconify icon="mdi:office-building-cog" />}
                        onClick={() => onOpenWorkspaceOverrides(policy)}
                    >
                        Workspace overrides{overrideCount > 0 ? ` (${overrideCount})` : ""}
                    </Button>
                    {hasWorkspaceOverride && (
                        <>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<Iconify icon="mdi:compare-horizontal" />}
                                onClick={() => onCompare(policy)}
                            >
                                Compare
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                color="warning"
                                startIcon={<Iconify icon="mdi:restore" />}
                                onClick={() => onResetToGlobal(policy)}
                            >
                                Reset to global
                            </Button>
                        </>
                    )}
                </Stack>

                {overridesLoading ? (
                    <Skeleton variant="rounded" height={32} width="60%" />
                ) : overrideCount > 0 ? (
                    <Stack spacing={0.5}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            Workspace overrides ({overrideCount}) — click a chip to view prompt
                        </Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {overrides.map((row) => {
                                const stale = isOverrideStale(row, globalVersion)
                                const label = resolveWorkspaceDisplayName(row.workspaceId, workspaceNameById)
                                const chip = (
                                    <Chip
                                        key={row.workspaceId}
                                        label={stale ? `${label} (stale)` : label}
                                        size="small"
                                        variant={row.workspaceId === selectedWorkspaceId ? "filled" : "outlined"}
                                        color={stale ? "error" : "warning"}
                                        onClick={() => onPreviewWorkspaceOverride(policy, row)}
                                        sx={{ cursor: "pointer" }}
                                    />
                                )
                                return stale ? (
                                    <Tooltip
                                        key={row.workspaceId}
                                        title={getStaleOverrideTooltip(row, globalVersion)}
                                        arrow
                                    >
                                        {chip}
                                    </Tooltip>
                                ) : (
                                    chip
                                )
                            })}
                        </Stack>
                    </Stack>
                ) : (
                    !overridesError && (
                        <Typography variant="caption" color="text.secondary">
                            No workspace overrides — all workspaces use the global prompt.
                        </Typography>
                    )
                )}

                <Divider />

                <ModifyPromptPolicyActions
                    policy={policy}
                    globalPolicies={globalPolicies}
                    selectedWorkspaceId={selectedWorkspaceId}
                    selectedWorkspaceName={selectedWorkspaceName}
                    overrideCount={overrideCount}
                    onSuccess={onSaveSuccess}
                    layout="toolbar"
                />
            </Stack>
        </Card>
    )
}

// ----------------------------------------------------------------------

const PromptPoliciesLoadingSkeleton = () => (
    <Stack spacing={2}>
        {[1, 2, 3].map((index) => (
            <Card key={index} sx={{ p: 3 }}>
                <Stack spacing={1}>
                    <Skeleton variant="text" width="40%" height={28} />
                    <Skeleton variant="text" width="70%" height={20} />
                    <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
                </Stack>
            </Card>
        ))}
    </Stack>
)

// ----------------------------------------------------------------------

const PromptPolicies = () => {
    const [policiesMap, setPoliciesMap] = useState<Record<string, PromptPolicy>>({})
    const [overridesByPolicy, setOverridesByPolicy] = useState<Record<string, OverridesLoadState>>({})
    const [selectedWorkspace, setSelectedWorkspace] = useState<WorkspaceOption | null>(null)
    const [workspaceOptions, setWorkspaceOptions] = useState<WorkspaceOption[]>([])
    const [workspaceNameById, setWorkspaceNameById] = useState<Record<string, string>>({})
    const [workspaceSearchLoading, setWorkspaceSearchLoading] = useState(false)
    const [workspaceTotalElements, setWorkspaceTotalElements] = useState(0)
    const workspaceSearchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [fetchErrorMessage, setFetchErrorMessage] = useState<string | null>(null)
    const [versionHistoryPolicy, setVersionHistoryPolicy] = useState<PromptPolicy | null>(null)
    const [workspaceOverridesPolicy, setWorkspaceOverridesPolicy] = useState<PromptPolicy | null>(null)
    const [promptPreview, setPromptPreview] = useState<PromptPreviewState | null>(null)
    const [compareState, setCompareState] = useState<PromptPolicyCompareState | null>(null)
    const [resetTarget, setResetTarget] = useState<{ policy: PromptPolicy; workspaceId: string } | null>(null)
    const [isResetting, setIsResetting] = useState(false)

    const selectedWorkspaceId = selectedWorkspace?.id ?? ""
    const selectedWorkspaceName = selectedWorkspace?.name ?? "workspace"

    const loadOverridesForPolicy = useCallback(async (policyName: string) => {
        setOverridesByPolicy((prev) => ({
            ...prev,
            [policyName]: { ...prev[policyName], overrides: prev[policyName]?.overrides ?? [], loading: true, error: undefined },
        }))
        try {
            const { overrides } = await fetchWorkspaceOverrides(policyName)
            setOverridesByPolicy((prev) => ({
                ...prev,
                [policyName]: { overrides, loading: false },
            }))
        } catch (err) {
            const message = err instanceof Error ? err.message : "Request failed"
            setOverridesByPolicy((prev) => ({
                ...prev,
                [policyName]: { overrides: [], loading: false, error: message },
            }))
        }
    }, [])

    const loadOverridesForPolicies = useCallback(
        async (policies: Record<string, PromptPolicy>) => {
            await Promise.all(Object.values(policies).map((p) => loadOverridesForPolicy(p.name)))
        },
        [loadOverridesForPolicy]
    )

    const loadPromptPolicies = useCallback(async () => {
        setIsLoading(true)
        setFetchErrorMessage(null)
        try {
            const fetchedPolicies = await fetchPromptPolicies()
            setPoliciesMap(fetchedPolicies)
            await loadOverridesForPolicies(fetchedPolicies)
        } catch {
            setFetchErrorMessage("Failed to load prompt policies. Please try again.")
        } finally {
            setIsLoading(false)
        }
    }, [loadOverridesForPolicies])

    const searchWorkspaces = useCallback(async (search: string) => {
        setWorkspaceSearchLoading(true)
        try {
            const response = await getInternalWorkspaces({ page: 0, size: 100, search: search || undefined })
            const payload = response?.data as InternalWorkspacesResponseData | undefined
            const content = payload?.content ?? []
            const options = content
                .map((item) => mapInternalWorkspaceOption(item))
                .filter((item): item is WorkspaceOption => item != null)
            setWorkspaceOptions(options)
            setWorkspaceNameById((prev) => mergeWorkspaceNameMap(prev, options))
            setWorkspaceTotalElements(payload?.totalElements ?? options.length)
        } catch {
            setWorkspaceOptions([])
        } finally {
            setWorkspaceSearchLoading(false)
        }
    }, [])

    const debouncedSearchWorkspaces = useCallback(
        (search: string) => {
            if (workspaceSearchDebounceRef.current) {
                clearTimeout(workspaceSearchDebounceRef.current)
            }
            workspaceSearchDebounceRef.current = setTimeout(() => {
                searchWorkspaces(search)
            }, 300)
        },
        [searchWorkspaces]
    )

    useEffect(() => {
        loadPromptPolicies()
    }, [loadPromptPolicies])

    useEffect(() => {
        searchWorkspaces("")
        return () => {
            if (workspaceSearchDebounceRef.current) {
                clearTimeout(workspaceSearchDebounceRef.current)
            }
        }
    }, [searchWorkspaces])

    useEffect(() => {
        if (!selectedWorkspace && workspaceOptions.length > 0) {
            setSelectedWorkspace(workspaceOptions[0])
        }
    }, [workspaceOptions, selectedWorkspace])

    const sortedPolicies = Object.values(policiesMap).sort((a, b) => a.policyType.localeCompare(b.policyType))

    const handleRefresh = async () => {
        await loadPromptPolicies()
        toast.success("Prompt policies refreshed")
    }

    const handlePreviewGlobalPrompt = (policy: PromptPolicy) => {
        setPromptPreview({ type: "global", policy })
    }

    const handlePreviewEffective = (policy: PromptPolicy) => {
        if (!selectedWorkspaceId) {
            toast.error("Select a workspace above to view the effective prompt.")
            return
        }
        const overrides = overridesByPolicy[policy.name]?.overrides ?? []
        const effective = getEffectivePrompt(policy, selectedWorkspaceId, overrides)
        setPromptPreview({
            type: "effective",
            policy,
            workspaceName: selectedWorkspaceName,
            source: effective.source,
            promptText: effective.text,
        })
    }

    const handlePreviewWorkspaceOverride = (policy: PromptPolicy, override: WorkspacePromptPolicyOverride) => {
        setPromptPreview({
            type: "workspace",
            policy,
            override,
            workspaceName: resolveWorkspaceDisplayName(override.workspaceId, workspaceNameById),
        })
    }

    const handleCompare = (policy: PromptPolicy) => {
        if (!selectedWorkspaceId) return
        const overrides = overridesByPolicy[policy.name]?.overrides ?? []
        const override = overrides.find((o) => o.workspaceId === selectedWorkspaceId)
        if (!override) return
        setCompareState({
            policy,
            workspaceName: selectedWorkspaceName,
            globalPrompt: policy.config?.prompt ?? "",
            workspacePrompt: override.config?.prompt ?? "",
        })
    }

    const handleConfirmReset = async () => {
        if (!resetTarget) return
        setIsResetting(true)
        try {
            await deleteWorkspacePromptOverride(resetTarget.policy.name, resetTarget.workspaceId)
            toast.success(`Reset "${formatPolicyTypeName(resetTarget.policy.policyType)}" to global for ${selectedWorkspaceName}`)
            setResetTarget(null)
            await loadPromptPolicies()
        } catch {
            // toast in service
        } finally {
            setIsResetting(false)
        }
    }

    const autocompleteOptions = useMemo(() => {
        if (selectedWorkspace && !workspaceOptions.some((w) => w.id === selectedWorkspace.id)) {
            return [selectedWorkspace, ...workspaceOptions]
        }
        return workspaceOptions
    }, [selectedWorkspace, workspaceOptions])

    return (
        <DashboardContent maxWidth="lg">
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, mt: 5 }}>
                <Box>
                    <Typography variant="h4">Prompt Policies</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Manage global LLM templates and per-workspace overrides.
                    </Typography>
                </Box>
                <Button
                    variant="outlined"
                    startIcon={<Iconify icon="mdi:refresh" />}
                    onClick={handleRefresh}
                    disabled={isLoading}
                >
                    Refresh
                </Button>
            </Stack>

            {fetchErrorMessage && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setFetchErrorMessage(null)}>
                    {fetchErrorMessage}
                </Alert>
            )}

            <PromptPoliciesGuidanceAlert />

            <Card sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, display: "block" }}>
                    Workspace for Modify
                </Typography>
                <Autocomplete
                    size="small"
                    value={selectedWorkspace}
                    onChange={(_, value) => setSelectedWorkspace(value)}
                    options={autocompleteOptions}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    filterOptions={(options) => options}
                    loading={workspaceSearchLoading}
                    onOpen={() => {
                        if (workspaceOptions.length === 0) {
                            searchWorkspaces("")
                        }
                    }}
                    onInputChange={(_, value, reason) => {
                        if (reason === "input") {
                            debouncedSearchWorkspaces(value)
                        }
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder="Search workspaces…"
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <>
                                        <Iconify
                                            icon="mdi:office-building"
                                            width={22}
                                            sx={{ color: "text.secondary", ml: 0.5, mr: 0.5 }}
                                        />
                                        {params.InputProps.startAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                    disabled={isLoading}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                    Required for Modify, effective prompt, compare, and reset. Does not affect Apply for all workspaces
                    or Version history.
                    {workspaceTotalElements > 50 && " Search to find more workspaces."}
                </Typography>
            </Card>

            {isLoading ? (
                <PromptPoliciesLoadingSkeleton />
            ) : (
                <Stack spacing={2}>
                    {sortedPolicies.length === 0 && !fetchErrorMessage && (
                        <Card sx={{ p: 4, textAlign: "center" }}>
                            <Typography color="text.secondary">No prompt policies found.</Typography>
                        </Card>
                    )}

                    {sortedPolicies.map((policy) => (
                        <PromptPolicyCard
                            key={policy.id}
                            policy={policy}
                            globalPolicies={policiesMap}
                            selectedWorkspaceId={selectedWorkspaceId}
                            selectedWorkspaceName={selectedWorkspaceName}
                            overridesState={
                                overridesByPolicy[policy.name] ?? { overrides: [], loading: false }
                            }
                            workspaceNameById={workspaceNameById}
                            onSaveSuccess={loadPromptPolicies}
                            onRetryOverrides={() => loadOverridesForPolicy(policy.name)}
                            onOpenVersionHistory={setVersionHistoryPolicy}
                            onOpenWorkspaceOverrides={setWorkspaceOverridesPolicy}
                            onPreviewWorkspaceOverride={handlePreviewWorkspaceOverride}
                            onPreviewGlobalPrompt={handlePreviewGlobalPrompt}
                            onPreviewEffective={handlePreviewEffective}
                            onCompare={handleCompare}
                            onResetToGlobal={(p) =>
                                selectedWorkspaceId &&
                                setResetTarget({ policy: p, workspaceId: selectedWorkspaceId })
                            }
                        />
                    ))}
                </Stack>
            )}

            <PromptPolicyVersionHistoryDialog
                open={!!versionHistoryPolicy}
                policy={versionHistoryPolicy}
                onClose={() => setVersionHistoryPolicy(null)}
                onSuccess={loadPromptPolicies}
            />

            <WorkspacePromptOverridesDialog
                open={!!workspaceOverridesPolicy}
                policy={workspaceOverridesPolicy}
                currentGlobalVersion={workspaceOverridesPolicy?.currentGlobalVersionNumber}
                workspaceNameById={workspaceNameById}
                onClose={() => setWorkspaceOverridesPolicy(null)}
                onSuccess={loadPromptPolicies}
                onSelectWorkspaceForEdit={(workspaceId) => {
                    const name = workspaceNameById[workspaceId]
                    if (name) {
                        setSelectedWorkspace({ id: workspaceId, name })
                    }
                }}
                onViewPrompt={(override, workspaceName) => {
                    if (workspaceOverridesPolicy) {
                        setPromptPreview({
                            type: "workspace",
                            policy: workspaceOverridesPolicy,
                            override,
                            workspaceName,
                        })
                    }
                }}
                onCompare={(override, workspaceName) => {
                    if (workspaceOverridesPolicy) {
                        setCompareState({
                            policy: workspaceOverridesPolicy,
                            workspaceName,
                            globalPrompt: workspaceOverridesPolicy.config?.prompt ?? "",
                            workspacePrompt: override.config?.prompt ?? "",
                        })
                    }
                }}
            />

            <WorkspacePromptPreviewDialog
                preview={promptPreview}
                onClose={() => setPromptPreview(null)}
                onSelectWorkspaceForEdit={(workspaceId) => {
                    const name = workspaceNameById[workspaceId]
                    if (name) {
                        setSelectedWorkspace({ id: workspaceId, name })
                    }
                }}
            />

            <PromptPolicyCompareDialog compare={compareState} onClose={() => setCompareState(null)} />

            <ConfirmDialog
                open={!!resetTarget}
                onClose={() => setResetTarget(null)}
                title="Reset to global prompt?"
                content={
                    <Typography variant="body2">
                        Remove the workspace override for <strong>{selectedWorkspaceName}</strong> on{" "}
                        <strong>
                            {resetTarget ? formatPolicyTypeName(resetTarget.policy.policyType) : "this policy"}
                        </strong>
                        . LLM calls will use the global prompt.
                    </Typography>
                }
                action={
                    <LoadingButton
                        variant="contained"
                        color="warning"
                        loading={isResetting}
                        onClick={handleConfirmReset}
                    >
                        Reset to global
                    </LoadingButton>
                }
            />
        </DashboardContent>
    )
}

export default PromptPolicies
